import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  Search,
  Checkbox,
  InlineLoading,
} from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import classNames from 'classnames';
import { debounce, isEqual } from 'lodash-es';

import {
  tcColorByModalOpenAtom,
  tcColorByRulesAtom,
  tcColorByUwiMapAtom,
  tcWellsFacetsAtom,
  tcEnabledWellFiltersAtom,
  tcMapFilterModeAtom,
  tcMapBoundsAtom,
  tcAoiPolygonAtom,
  tcInventoryGunBarrelScopeAtom,
  tcInventoryAreaFormationsAtom,
} from './atoms';
import {
  ColorByTarget,
  ColorByConfig,
  FilterCategory,
  CategoricalFacet,
  TcWellsFacets,
  TcWellsQueryResponse,
} from '../types';
import { TC_WELL_FILTER_CATEGORIES } from './utils';
import { DEFAULT_COLORS } from 'components/color-picker';
import styles from './tc-color-by-modal.module.scss';
import { TypeCurveHeader } from 'types/api-responses';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import { buildTcWellsQueryPayload } from '../build-tc-wells-query-payload';
import request from 'lib/request';

const QUERY_PATH = '/queries/wells';

const COLOR_BY_CATEGORIES = TC_WELL_FILTER_CATEGORIES.filter(
  (c) => c.type === 'picklist'
);

const APPLY_TO_TARGETS: { id: ColorByTarget; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'probitPlot', label: 'Probit Plot' },
  { id: 'monthlyVolumes', label: 'Monthly Volumes' },
  { id: 'ratios', label: 'Ratios' },
  { id: 'crossPlot', label: 'Cross Plot' },
];

interface FacetOption {
  id: string;
  name: string;
}

function getFacetPicklistOptions(
  category: FilterCategory,
  facets: TcWellsFacets | null
): FacetOption[] {
  if (!facets || !category.facetKey) return [];
  const facet = facets[category.facetKey] as CategoricalFacet | undefined;
  if (!facet?.buckets) return [];
  return facet.buckets
    .map((b) => ({ id: b.id, name: b.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const DEFAULT_APPLY_TO: Record<ColorByTarget, boolean> = {
  map: false,
  probitPlot: true,
  monthlyVolumes: true,
  ratios: true,
  crossPlot: false,
};

const CategoryList = ({
  activeCategory,
  onSelect,
  availableCategories,
}: {
  activeCategory: string;
  onSelect: (id: string) => void;
  availableCategories: typeof COLOR_BY_CATEGORIES;
}) => {
  const [search, setSearch] = useState('');
  const categories = availableCategories.filter((cat) =>
    cat.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.categoriesInner}>
      <div className={styles.categoriesSearch}>
        <Search
          labelText="Search filters"
          placeholder="Search Filter Names"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className={styles.categoryList}>
        {categories.map((cat) => (
          <li key={cat.id} className={styles.categoryItem}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={classNames(styles.categoryButton, {
                [styles.categoryButtonActive]: activeCategory === cat.id,
              })}
            >
              {cat.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

function getValueColor(
  value: string,
  index: number,
  existingColors: Record<string, string>,
  // Inventory Workspace: backend-configured colour per formation, so a formation
  // seeds its own swatch colour instead of the generic palette.
  predefinedColors?: Record<string, string>
): string {
  return (
    existingColors[value] ??
    predefinedColors?.[value] ??
    DEFAULT_COLORS[index % DEFAULT_COLORS.length]
  );
}

export const AttributeForm = ({
  activeCategory,
  selectedValues,
  onChange,
  facets,
  existingValueColors,
}: {
  activeCategory: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  facets: TcWellsFacets | null;
  existingValueColors: Record<string, string>;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Inventory Workspace: colour-by Formation uses the selected area's formations
  // (the sidebar tree) for both the option list and each swatch's default colour.
  // `null` off the Inventory Workspace, so plain Type Curve pages are unchanged.
  const areaFormations = useAtomValue(tcInventoryAreaFormationsAtom);
  const category = COLOR_BY_CATEGORIES.find((c) => c.id === activeCategory);
  if (!category) return null;

  const useAreaFormations =
    areaFormations !== null && category.id === 'formation';
  const options = useAreaFormations
    ? areaFormations.map((f) => ({ id: f.formation, name: f.formation }))
    : getFacetPicklistOptions(category, facets);
  const predefinedColors = useAreaFormations
    ? Object.fromEntries(areaFormations.map((f) => [f.formation, f.color]))
    : undefined;
  const nameById = new Map(options.map((opt) => [opt.id, opt.name]));

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIds = filteredOptions.map((o) => o.id);

  const allSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedValues.includes(id));

  const handleSelectAll = () => {
    if (allSelected) {
      onChange(selectedValues.filter((v) => !filteredIds.includes(v)));
    } else {
      onChange([...new Set([...selectedValues, ...filteredIds])]);
    }
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <div className={styles.formWrapper}>
      <h4 className={styles.formTitle}>{category.label}</h4>
      <div className={styles.distinctColors}>
        <span className={styles.distinctColorsLabel}>Distinct Colors</span>
        {selectedValues.length === 0 ? (
          <span className={styles.distinctColorsEmpty}>No values selected</span>
        ) : (
          <div className={styles.distinctColorsList}>
            {selectedValues.map((val, idx) => (
              <div key={val} className={styles.distinctColorRow}>
                <span
                  className={styles.distinctColorSwatch}
                  style={{
                    backgroundColor: getValueColor(
                      val,
                      idx,
                      existingValueColors,
                      predefinedColors
                    ),
                  }}
                />
                <span className={styles.distinctColorName}>
                  {nameById.get(val) ?? val}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.formContent}>
        <Search
          labelText={`Search ${category.label.toLowerCase()}`}
          placeholder={`Search ${category.label.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="sm"
        />
        <div className={styles.selectRow}>
          <button
            type="button"
            className={styles.selectAllLink}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <span className={styles.resultCount}>
            Top {filteredOptions.length} results shown
          </span>
        </div>
        <div className={styles.optionsList}>
          {filteredOptions.map((option) => (
            <Checkbox
              key={option.id}
              id={`color-by-${activeCategory}-${option.id}`}
              labelText={option.name}
              checked={selectedValues.includes(option.id)}
              onChange={(_evt, { checked }) => {
                onChange(
                  checked
                    ? [...selectedValues, option.id]
                    : selectedValues.filter((v) => v !== option.id)
                );
              }}
            />
          ))}
          {filteredOptions.length === 0 && (
            <p className={styles.noResults}>No options available</p>
          )}
        </div>
      </div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={handleClear}
          disabled={selectedValues.length === 0}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

const ApplyToPanel = ({
  applyTo,
  onChange,
}: {
  applyTo: Record<ColorByTarget, boolean>;
  onChange: (applyTo: Record<ColorByTarget, boolean>) => void;
}) => {
  const hasAnyApplyTo = Object.values(applyTo).some(Boolean);

  return (
    <div className={styles.applyToPanel}>
      <h4 className={styles.applyToTitle}>Apply To</h4>
      <div className={styles.applyToContent}>
        <div className={styles.applyToList}>
          {APPLY_TO_TARGETS.map(({ id, label }) => (
            <div key={id} className={styles.applyToItem}>
              <Checkbox
                id={`apply-to-${id}`}
                labelText={label}
                checked={applyTo[id]}
                onChange={(_evt, { checked }) => {
                  onChange({ ...applyTo, [id]: checked });
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={() => onChange({ ...DEFAULT_APPLY_TO })}
          disabled={!hasAnyApplyTo}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export const TcColorByModal = ({
  editingRuleId,
  onClose,
  typeCurveHeader,
}: {
  editingRuleId?: string | null;
  onClose?: () => void;
  typeCurveHeader?: TypeCurveHeader | null;
}) => {
  const [isOpen, setIsOpen] = useAtom(tcColorByModalOpenAtom);
  const [rules, setRules] = useAtom(tcColorByRulesAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);
  const [existingColorByUwiMap, setColorByUwiMap] =
    useAtom(tcColorByUwiMapAtom);

  // Read shared filter atoms for color-by UWI fetch
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  // Disabled filters are excluded here too, so the preview counts match the page.
  const filters = useAtomValue(tcEnabledWellFiltersAtom);
  const mapFilterMode = useAtomValue(tcMapFilterModeAtom);
  const mapBounds = useAtomValue(tcMapBoundsAtom);
  const aoiPolygon = useAtomValue(tcAoiPolygonAtom);
  // Inventory Workspace: backend swatch colour per formation, used to seed the
  // persisted colour of a Color-by-Formation rule (null off the workspace).
  const areaFormations = useAtomValue(tcInventoryAreaFormationsAtom);

  const editingRule = useMemo(() => {
    return editingRuleId
      ? (rules.find((r) => r.ruleId === editingRuleId) ?? null)
      : null;
  }, [editingRuleId, rules]);

  // The Inventory Workspace offers NO well-qualifier colouring in any case, so
  // that attribute is dropped from the picker there. Non-null only on the
  // Inventory Workspace; plain Type Curve pages keep the full list.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const availableCategories = useMemo(() => {
    if (inventoryScope !== null) {
      return COLOR_BY_CATEGORIES.filter((c) => c.id !== 'wellQualifier');
    }
    return COLOR_BY_CATEGORIES;
  }, [inventoryScope]);

  const [activeCategory, setActiveCategory] = useState<string>(
    availableCategories[0]?.id ?? ''
  );
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [applyTo, setApplyTo] = useState<Record<ColorByTarget, boolean>>({
    ...DEFAULT_APPLY_TO,
  });

  // Local color-by UWI fetch state
  const [localColorByUwiMap, setLocalColorByUwiMap] = useState<
    Record<string, string[]>
  >({});
  const [isFetching, setIsFetching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editingRule) {
      setActiveCategory(editingRule.attribute);
      setSelectedValues([...editingRule.selectedValues]);
      setApplyTo({ ...editingRule.applyTo });
    } else {
      setActiveCategory(availableCategories[0]?.id ?? '');
      setSelectedValues([]);
      // Default all targets to true; multiple rules can share targets
      // and later rules override earlier ones at the UWI level.
      setApplyTo({
        map: true,
        probitPlot: false,
        monthlyVolumes: false,
        ratios: false,
        crossPlot: true,
      });
    }
    setLocalColorByUwiMap({});
    setIsFetching(false);
  }, [isOpen, editingRule, availableCategories]);

  // Debounced color-by UWI fetch
  const fetchColorByUwis = useCallback(
    async (category: string, values: string[]) => {
      if (!typeCurveHeader || !selectedDcaScenario || values.length === 0) {
        setLocalColorByUwiMap({});
        setIsFetching(false);
        return;
      }

      const cat = TC_WELL_FILTER_CATEGORIES.find((c) => c.id === category);
      if (!cat) {
        setIsFetching(false);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsFetching(true);

      const newMap: Record<string, string[]> = {};

      try {
        await Promise.all(
          values.map(async (value) => {
            if (controller.signal.aborted) return;

            const colorByFilter = { ...filters, [cat.param]: [value] };
            const payload = buildTcWellsQueryPayload({
              typeCurveHeader,
              dcaScenarioId: selectedDcaScenario.id,
              filters: colorByFilter,
              mapFilterMode,
              mapBounds,
              aoiPolygon,
              page: 1,
              pageSize: 1,
              limit: 1,
            });

            const response = await request.post<TcWellsQueryResponse, unknown>(
              QUERY_PATH,
              { query: payload },
              controller
            );

            if (controller.signal.aborted) return;
            if (response.type !== 'success') return;

            const uwis = (response.data.filteredUwis ?? []).map((w) => w.uwi);
            newMap[`${category}:${value}`] = uwis;
          })
        );

        if (!controller.signal.aborted) {
          setLocalColorByUwiMap(newMap);
        }
      } finally {
        if (!controller.signal.aborted) setIsFetching(false);
      }
    },
    [
      typeCurveHeader,
      selectedDcaScenario,
      filters,
      mapFilterMode,
      mapBounds,
      aoiPolygon,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((category: string, values: string[]) => {
      void fetchColorByUwis(category, values);
    }, 500),
    [fetchColorByUwis]
  );

  // Trigger debounced fetch when selected values or active category change
  useEffect(() => {
    if (!isOpen) return;
    if (selectedValues.length === 0) {
      setLocalColorByUwiMap({});
      setIsFetching(false);
      return;
    }
    setIsFetching(true);
    debouncedFetch(activeCategory, selectedValues);
    return () => {
      debouncedFetch.cancel();
    };
  }, [isOpen, activeCategory, selectedValues, debouncedFetch]);

  const handleApply = () => {
    if (isFetching) return;

    const existingValueColors = editingRule?.valueColors ?? {};
    const valueColors: Record<string, string> = { ...existingValueColors };
    // Color-by-Formation on the Inventory Workspace seeds each formation its
    // backend swatch colour before falling back to the generic palette.
    const predefinedColors =
      areaFormations !== null && activeCategory === 'formation'
        ? Object.fromEntries(areaFormations.map((f) => [f.formation, f.color]))
        : undefined;
    selectedValues.forEach((val, idx) => {
      if (!valueColors[val]) {
        valueColors[val] =
          predefinedColors?.[val] ??
          DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
      }
    });
    // Remove colors for deselected values
    for (const key of Object.keys(valueColors)) {
      if (!selectedValues.includes(key)) delete valueColors[key];
    }

    const newRule: ColorByConfig = {
      ruleId:
        editingRule?.ruleId ??
        `${activeCategory}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      attribute: activeCategory,
      selectedValues,
      valueColors,
      applyTo,
      enabled: editingRule?.enabled ?? true,
    };

    if (editingRule) {
      setRules(
        rules.map((r) => (r.ruleId === editingRule.ruleId ? newRule : r))
      );
    } else {
      setRules([...rules, newRule]);
    }

    // Write local color-by UWI map to global atom (merge with existing).
    // Skip the write when the merged result is unchanged — avoids triggering
    // all six derived color map atoms when editing a disabled rule.
    const merged = { ...existingColorByUwiMap, ...localColorByUwiMap };
    if (!isEqual(merged, existingColorByUwiMap)) {
      setColorByUwiMap(merged);
    }

    setIsOpen(false);
    onClose?.();
  };

  // Must close unconditionally. ComposedModal drops its own internal open state
  // whenever onClose returns anything but `false`, and only re-syncs from the
  // `open` prop when that prop CHANGES — so bailing out here left the atom true
  // against a closed modal, and re-setting it true was a no-op that could never
  // reopen it. The in-flight fetch is aborted instead.
  const handleCancel = () => {
    abortRef.current?.abort();
    setIsFetching(false);
    setIsOpen(false);
    onClose?.();
  };

  const handleReset = () => {
    setSelectedValues([]);
    setApplyTo({ ...DEFAULT_APPLY_TO });
  };

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleCancel}
      size="lg"
      className={styles.modal}
    >
      <ModalHeader title="Color by Attribute" />
      <ModalBody>
        <div className={styles.content}>
          <div className={styles.categories}>
            <CategoryList
              activeCategory={activeCategory}
              availableCategories={availableCategories}
              onSelect={(id) => {
                setActiveCategory(id);
                if (!editingRule || editingRule.attribute !== id) {
                  setSelectedValues([]);
                }
              }}
            />
          </div>
          <div className={styles.form}>
            <AttributeForm
              key={activeCategory}
              activeCategory={activeCategory}
              selectedValues={selectedValues}
              onChange={setSelectedValues}
              facets={facets}
              existingValueColors={editingRule?.valueColors ?? {}}
            />
            {isFetching && <InlineLoading description="Loading well data..." />}
          </div>
          <div className={styles.applyTo}>
            <ApplyToPanel applyTo={applyTo} onChange={setApplyTo} />
          </div>
        </div>
      </ModalBody>
      <div className={styles.customFooter}>
        <Button
          size="lg"
          kind="ghost"
          className={styles.footerButton}
          onClick={handleCancel}
          disabled={isFetching}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="secondary"
          className={styles.footerButton}
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={styles.footerButton}
          onClick={handleApply}
          disabled={isFetching}
        >
          {isFetching ? 'Loading...' : 'Apply'}
        </Button>
      </div>
    </ComposedModal>
  );
};
